import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LoanStatusType, LoanType } from '../common/LoanConstants';
import { BfiDataGrid, Column } from '../../common/SharedComponents';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { getLoanGridSettingId, onBorrowerCellRender, onActionCellRender, OnOpenLoanDetail } from '../common/LoanFunctions';
import { LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { LoanApprovedOrNoteDateColumn } from '../common/components/LoanApprovedOrNoteDateColumn';
import { onLoanTermCellRender } from '../common/functions/onLoanTermCellRender';
import { onRepaidDateCellRender } from '../common/functions/onRepaidDateCellRender';
import { onExtensionCellRender } from '../common/functions/onExtensionCellRender';

const MasterNotesTable = ({ gridStore, statusId = LoanStatusType.Outstanding }) => {
    const searchState = useSelector((state) => state.searchState);
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => {
        criteria.statusId = statusId;
        return loanApi.getGrid_MasterNotes(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loan);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    return (
        <BfiDataGrid
            gridType={getLoanGridSettingId(LoanType.MasterNote, statusId)}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onCellDblClick={(e) => OnOpenLoanDetail(e, location)}
            key={'MasterNotesTable'}
        >
            <Column
                key='borrowerEntity'
                dataField='borrowerEntity'
                caption='Entity'
                alignment='left'
                cellRender={onBorrowerCellRender}
                minWidth={120}
            />
            <Column
                key='borrowerCity'
                dataField='borrowerCity'
                caption='Borr. City'
                dataType='string'
                width={90}
            />
            <Column
                key='borrowerState'
                dataField='borrowerState'
                caption='Borr. State'
                dataType='string'
                width={70}
            />
            <Column
                key='bfiNumber'
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={120}
            />
            <Column
                key='loanNumber'
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            {LoanCommitOrApprovalColumn({ statusId })}
            {LoanApprovedOrNoteDateColumn({ statusId })}
            <Column
                key='term'
                dataField='initialTermMonths'
                caption='Term'
                dataType='string'
                width={60}
                alignment={'left'}
                cellRender={onLoanTermCellRender}
            />
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='repaidDate'
                    caption='Repaid Date'
                    dataType='string'
                    width={110}
                    alignment={'left'}
                    cellRender={onRepaidDateCellRender}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    key='totalRepaidAmount'
                    dataField='totalRepaidAmount'
                    caption='Repaid Amt'
                    dataType='number'
                    format='currency'
                    width={110}
                    alignment={'left'}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='extensionNumber'
                    caption='Ext.'
                    dataType='string'
                    width={40}
                    alignment='left'
                    cellRender={onExtensionCellRender}
                />
            )}
            {/* TODO: Future work related to Defaults */}
            {/* {statusId === LoanStatusType.Repaid && (
                    <Column
                        dataField='defaults'
                        caption='Defaults'
                        dataType='string'
                        width={110}
                    />
                )} */}
            <Column
                key='interestRate'
                dataField='interestRate'
                caption='Rate'
                dataType='number'
                format={{ type: 'percent', precision: 2 }}
                width={60}
                alignment={'left'}
            />
            <Column
                key='pointOfContact'
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={120}
            />
            <Column
                key='ownerName'
                dataField='ownerName'
                caption='User'
                dataType='string'
                width={120}
            />
            <Column
                key='broker'
                dataField='broker'
                dataType='string'
                width={120}
            />
            <Column
                key='lostType'
                dataField='lostType'
                caption={'Reason'}
                dataType='string'
                width={80}
                visible={statusId === LoanStatusType.Lost}
            />
            <Column
                key='lostReason'
                dataField='lostReason'
                caption='Details'
                dataType='string'
                width={200}
                visible={statusId === LoanStatusType.Lost}
            />
            <Column
                alignment='center'
                cellRender={onActionCellRender}
                width={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                key='actions'
                fixed={true}
                fixedPosition={'right'}
            />
        </BfiDataGrid>
    );
};

export default MasterNotesTable;
